import { gsap, Power4 } from "gsap";
import * as THREE from "three";

export class FlyingPapers {
    constructor (params) {
        this.scene = params.scene
        this.emitter = this.createEmitter();
        this.particle = params.particleBase || this.createBaseParticle();
        this.count = 100;

        this.dummy = new THREE.Object3D();
        this.aliveParticles = this.count;
        
        this.emitterProgress = 0;
        this.emitterRotations = 5 * 6.28;
        this.emitterDistance = 1
        this.emitterOrbit = 0
        this.emitterMovDuration = 4
        this.emitterElevStart = 20

        this.running = false;

        this.initArrays()
        this.createParticles()
        this.createLight()

    }

    createLight(){
        this.ambiantLight = new THREE.AmbientLight(0xffffff, .1)
        this.light = new THREE.DirectionalLight();
        this.scene.add(this.light)
        this.scene.add(this.ambiantLight)
    }

    initArrays(){
        this.elevations = []
        this.orbitsAngles = []
        this.distances = []
        this.scales = []
        this.rotations = []
        this.seeds = []

        this.orbitsSpeeds = []
        this.elevationsSpeeds = []
        this.distancesSpeeds = []
        this.scalesSpeeds = []
        this.rotationsSpeeds = []

        this.globalTimes = []
        this.flyingTimes = []
        this.durations = []
        this.progresses = []
        this.delays = []
        this.isAlive = []
        this.hasEnded = []
        this.hasStarted = []
    }

    createEmitter(){
        const geom = new THREE.BoxGeometry(4,4,4)
        const mat = new THREE.MeshBasicMaterial()
        const emitter = new THREE.Mesh(geom, mat)
        emitter.geometry.computeBoundingBox();
        emitter.position.y = this.emitterElevStart
        //this.scene.add(emitter)
        return emitter
    }

    createBaseParticle(){
        const geom = new THREE.PlaneGeometry(3,2)
        const mat = new THREE.MeshLambertMaterial({
            color: 0xffffff, 
            side:THREE.DoubleSide, 
        })
        const particle = new THREE.Mesh(geom, mat)
        return particle
    }

    createParticles() {
        const geometryParticle = this.particle.geometry.clone();
        const materialParticle = this.particle.material.clone();
        this.particles = new THREE.InstancedMesh(geometryParticle, materialParticle, this.count);
        this.scene.add(this.particles)
    }

    updateParticles(dt) {

        if (!this.running) return;

        // no more particles alive
        if (this.aliveParticles === 0){
            return;
        }

        for (let i = 0; i < this.count; i++) {
            
            // particle has been marked as ended
            if (this.hasEnded[i] === 1) { 
                continue
            }
            
            // particle has died
            if (this.isAlive[i] === 0){ 
                continue
            }
            
            // particle waiting to be launched
            if (this.globalTimes[i] < this.delays[i]) { 
                this.globalTimes[i] += dt
                continue
            }
            
            if (!this.hasStarted[i] && this.globalTimes[i] >= this.delays[i]){
                this.hasStarted[i] = true
                this.distances[i] = this.emitterDistance
                this.orbitsAngles[i] = this.emitterOrbit
                this.elevations[i] = this.emitter.position.y
            }

            // particle has just ended
            if (this.flyingTimes[i] > this.durations[i]){
                this.killParticle(i)
                continue;
            }

            // progress 0 -> 1 
            let progress = this.flyingTimes[i] / this.durations[i]
            let progInvert = 1 - progress
            //progress = Math.sin(progress * Math.PI/2);
            
            //*
            /* const newScale = this.calculateVectorProgress(this.scales[i], this.targetScale, progress, {x:0, y:0, z:0}); 
            this.dummy.scale.copy(newScale) */

            this.elevations[i] += this.elevationsSpeeds[i] * dt * progInvert
            this.elevations[i] -= 10 * dt
            this.elevations[i] += Math.sin( progress + this.seeds[i]) * .05
            this.orbitsAngles[i] += this.orbitsSpeeds[i] * dt * Math.pow(progInvert, 2.)
            this.distances[i] += this.distancesSpeeds[i] * dt * progInvert

            const posX = Math.cos(this.orbitsAngles[i]) * this.distances[i]
            const posZ = Math.sin(this.orbitsAngles[i]) * this.distances[i]

            this.dummy.position.set(posX, this.elevations[i], posZ)
            let s = Math.sin(progress * Math.PI)
            s = Math.pow(s, .3)
            this.dummy.scale.set(s,s,s)
            this.dummy.rotation.x = this.rotations[i].x + dt * this.rotationsSpeeds[i];
            this.dummy.rotation.y = this.rotations[i].y + dt * this.rotationsSpeeds[i];
            this.dummy.rotation.z = this.rotations[i].z + dt * this.rotationsSpeeds[i];
            
            this.dummy.updateMatrix();
            this.particles.setMatrixAt(i, this.dummy.matrix);
            
            this.rotations[i].copy(this.dummy.rotation)
            this.globalTimes[i] += dt
            this.flyingTimes[i] += dt
            //*/
        }
        this.particles.instanceMatrix.needsUpdate = true

    }

    calculateVectorProgress(start, end, t, wave = {x:1, y:0, z:1}){
        let pos = new THREE.Vector3();
        pos.x = this.calculateProgress(start.x, end.x, t, wave.x);
        pos.y = this.calculateProgress(start.y, end.y, t, wave.y);
        pos.z = this.calculateProgress(start.z, end.z, t, wave.z);
        return pos;
    }
    calculateProgress(start, end, t, wave = 0){
        return start+(end-start)*t + Math.sin(t * 3 * Math.PI) * wave;
    }

    launch(params = {}){
        //console.log("lauch flying papers")

        const delay = params.delay || 0
        
        this.aliveParticles = this.count

        for (let i = 0; i < this.count; i++) {
            this.resetParticle(i)
        }
        gsap.to(this, {
            emitterProgress:1,
            duration : this.emitterMovDuration,
            ease: Power4.easeInOut,
            delay,

            onStart : () => {
                this.running = true;
            },

            onUpdate: ()=>{
                this.emitterOrbit = this.emitterProgress * this.emitterRotations
                this.emitter.position.y = this.emitterElevStart - this.emitterProgress * this.emitterElevStart;
                this.emitter.position.x = Math.cos(this.emitterOrbit) * this.emitterDistance;
                this.emitter.position.z = Math.sin(this.emitterOrbit) * this.emitterDistance;
            }
        })
    }

    resetParticle(id) {

        const tau = 6.28
        
        // position
        
        this.elevations[id] = 0
        this.orbitsAngles[id] = 0
        this.elevationsSpeeds[id] = 10 + Math.random() * 12
        this.distancesSpeeds[id] = 5 + Math.random() * 10
        this.orbitsSpeeds[id] = .1 + Math.random() * 10
        this.rotationsSpeeds[id] = .1 + Math.random() * 3

        // rotation
        this.rotations[id] = new THREE.Vector3( Math.random() * tau ,  Math.random() * tau,  Math.random() * tau)

        // scales
        this.scales[id] = 1 + Math.random() * 1

        this.delays[id] = Math.pow(Math.random(), 2) * this.emitterMovDuration;
        this.seeds[id] = Math.random();
        this.durations[id] = 8;
        this.isAlive[id] = 1
        this.hasEnded[id] = 0
        this.globalTimes[id] = 0
        this.flyingTimes[id] = 0
    }

    killParticle(id){
        //console.log("kill particle", id)
        this.hasEnded[id] = 1;
        this.isAlive[id] = 0
        this.globalTimes[id] = 0
        this.flyingTimes[id] = 0
        this.hasStarted[id] = false
        this.dummy.scale.set(0,0,0)
        this.dummy.updateMatrix();
        this.particles.setMatrixAt(id, this.dummy.matrix);
        //this.scales[id].copy(this.dummy.scale);
        this.aliveParticles--;
        if (this.aliveParticles <= 0){
            this.running = false;
            console.log("no more particles")
        }
    }

}